import { ChartTooltip } from "@similarweb/ui-components";
import type { ColumnDefinition } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    minAbbrNumberFilter,
    percentagePointFilter,
    percentageSignFilter,
    yAxisChangeFilter,
} from "app/filters/ngFilters";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";

import { TooltipTitle } from "../table/bubble-chart/chart/tooltip/BubbleChartTooltipTitle";

import type { SwitchGraphDomainData } from "./types";

interface TooltipRow {
    metric: { label: string };
    value: { value: string };
    change: { value: string };
}

const calculateChangeValue = (
    firstValue: number | undefined,
    lastValue: number | undefined,
    isPercentage: boolean,
): string | null => {
    // Validate values
    if (
        firstValue === undefined ||
        lastValue === undefined ||
        isNaN(firstValue) ||
        isNaN(lastValue) ||
        (!isPercentage && firstValue === 0)
    ) {
        return "-";
    }

    if (isPercentage) {
        // Calculate percentage point difference
        const difference = (lastValue - firstValue) * 100;
        if (isNaN(difference) || difference === 0) {
            return "-";
        }
        return percentagePointFilter(lastValue, firstValue);
    } else {
        // Calculate percentage change
        const change = (lastValue - firstValue) / firstValue;
        if (isNaN(change) || change === 0) {
            return "-";
        }
        return yAxisChangeFilter()(change, 2);
    }
};

const metricColumn: ColumnDefinition<TooltipRow> = {
    key: "metric",
    get title() {
        return translate("Metric");
    },
    selector: "metric" as const,
    align: "left",
    size: "2fr",
    render: ({ metric }) => {
        return <ChartTooltip.LabelCell label={metric.label} />;
    },
};

const valueColumn: ColumnDefinition<TooltipRow> = {
    key: "value",
    title: "Value",
    selector: "value" as const,
    align: "right",
    size: "1fr",
    borderRight: true,
    render: ({ value }) => {
        return <ChartTooltip.ValueCell value={value.value} />;
    },
};

const changeColumn: ColumnDefinition<TooltipRow> = {
    key: "change",
    get title() {
        return translate("Change");
    },
    selector: "change" as const,
    align: "right",
    size: "1fr",
    render: ({ change }) => {
        const { value } = change;
        if (value === null) {
            return null;
        }

        return (
            <ChartTooltip.ChangeCell
                value={value}
                isDecrease={Number(value.replace(/[^0-9.-]/g, "")) < 0}
                // @ts-expect-error FIXME: value is string but compared to number 0 — pre-existing behavior
                unsigned={value === 0 || value === "-"}
            />
        );
    },
};

const getColumnsDefinition = (showChangeColumn: boolean) => {
    const adjustedValueColumn = showChangeColumn ? valueColumn : { ...valueColumn, borderRight: false };
    return showChangeColumn ? [metricColumn, adjustedValueColumn, changeColumn] : [metricColumn, adjustedValueColumn];
};

const getData = (totalMetricsValue: string, changeValue: string | null, metricLabel: string) => {
    return [
        {
            metric: {
                label: metricLabel,
            },
            value: {
                value: totalMetricsValue,
            },
            change: {
                value: changeValue,
            },
        },
    ];
};

interface Props {
    websiteTitle: string;
    websiteIcon?: string;
    subDisplayText?: string;
    currentValue: number;
    rawDomainData?: SwitchGraphDomainData;
    chartDisplayType: EChartDisplayType;
    totalMarketVisits?: number;
}

export const MarketSharesSinglePeriodTooltipFormatter = ({
    websiteTitle,
    websiteIcon,
    subDisplayText,
    currentValue,
    rawDomainData,
    chartDisplayType,
    totalMarketVisits,
}: Props) => {
    const isPercentage = chartDisplayType === EChartDisplayType.PERCENTAGE;
    let totalMetricsValue: string;
    let changeValue: string | null = null;

    // Calculate value and change from rawDomainData if available
    if (rawDomainData?.Metrics && rawDomainData.Metrics.length > 0) {
        const metrics = rawDomainData.Metrics[0]; // Get first WebSource

        if (isPercentage) {
            const shareValue = metrics.TotalMetrics?.AvgMonthVisitsShare ?? currentValue;
            totalMetricsValue = percentageSignFilter()(shareValue, 2);

            if (metrics.AvgMonthVisits && metrics.AvgMonthVisits.length > 0) {
                const firstValue = metrics.AvgMonthVisits[0].Value.Share;
                const lastValue = metrics.AvgMonthVisits[metrics.AvgMonthVisits.length - 1].Value.Share;
                changeValue = calculateChangeValue(firstValue, lastValue, true);
            }
        } else {
            const absoluteValue = metrics.TotalMetrics?.AvgMonthVisits ?? currentValue;
            totalMetricsValue = minAbbrNumberFilter()(absoluteValue);

            if (metrics.AvgMonthVisits && metrics.AvgMonthVisits.length > 0) {
                const firstValue = metrics.AvgMonthVisits[0].Value.Absolute;
                const lastValue = metrics.AvgMonthVisits[metrics.AvgMonthVisits.length - 1].Value.Absolute;
                changeValue = calculateChangeValue(firstValue, lastValue, false);
            }
        }
    } else if (totalMarketVisits !== undefined && currentValue) {
        // For "Others", calculate based on chart display type
        if (isPercentage) {
            totalMetricsValue = percentageSignFilter()(currentValue, 2);
        } else {
            const absoluteValue = currentValue * totalMarketVisits;
            totalMetricsValue = minAbbrNumberFilter()(absoluteValue);
        }
    } else {
        // Fallback when no raw data is available
        totalMetricsValue = isPercentage
            ? percentageSignFilter()(currentValue, 2)
            : minAbbrNumberFilter()(currentValue);
    }

    // Show change column only when we have raw domain data
    const showChangeColumn = !!rawDomainData;

    const metricLabel = translate(
        isPercentage
            ? "market.players.market.shares.chart.tooltip.traffic.share"
            : "market.players.market.shares.chart.tooltip.monthly.visits",
    );

    return (
        <ChartTooltip
            width={320}
            title={
                <TooltipTitle
                    entityDisplayName={websiteTitle}
                    favicon={websiteIcon}
                    subtitle={subDisplayText}
                />
            }
            columnDefinition={getColumnsDefinition(showChangeColumn)}
            data={getData(totalMetricsValue, changeValue, metricLabel)}
        />
    );
};
